import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CostBreakdownItem } from '../../api/cost';

interface CostBreakdownProps {
  breakdown: CostBreakdownItem[];
  currency?: string;
  totalMonthly: number;
  totalHourly: number;
}

interface GroupedCosts {
  [provider: string]: {
    total: number;
    items: CostBreakdownItem[];
  };
}

export const CostBreakdown: React.FC<CostBreakdownProps> = ({
  breakdown,
  currency = 'USD',
  totalMonthly,
  totalHourly,
}) => {
  const { t } = useTranslation('finops');
  const [expandedProvider, setExpandedProvider] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'cost' | 'name'>('cost');

  // Grouper par provider
  const groupedByProvider: GroupedCosts = breakdown.reduce((acc, item) => {
    const provider = item.provider || 'unknown';
    if (!acc[provider]) {
      acc[provider] = { total: 0, items: [] };
    }
    acc[provider].items.push(item);
    acc[provider].total += item.monthly || 0;
    return acc;
  }, {} as GroupedCosts);

  // Trier les providers par coût décroissant
  const sortedProviders = Object.entries(groupedByProvider).sort(
    (a, b) => b[1].total - a[1].total
  );

  const formatCost = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getProviderColor = (provider: string): string => {
    const colors: { [key: string]: string } = {
      azure: '#0078D4',
      aws: '#FF9900',
      gcp: '#4285F4',
    };
    return colors[provider.toLowerCase()] || '#666666';
  };

  const getProviderName = (provider: string): string => {
    const names: { [key: string]: string } = {
      azure: 'Microsoft Azure',
      aws: 'Amazon Web Services',
      gcp: 'Google Cloud Platform',
    };
    return names[provider.toLowerCase()] || provider.toUpperCase();
  };

  const getProviderIcon = (provider: string): string => {
    const icons: { [key: string]: string } = {
      azure: '☁️',
      aws: '🟠',
      gcp: '🔵',
    };
    return icons[provider.toLowerCase()] || '☁️';
  };

  const toggleProvider = (provider: string) => {
    setExpandedProvider(expandedProvider === provider ? null : provider);
  };

  const sortItems = (items: CostBreakdownItem[]) => {
    return [...items].sort((a, b) => {
      if (sortBy === 'cost') {
        return (b.monthly || 0) - (a.monthly || 0);
      } else {
        return (a.name || '').localeCompare(b.name || '');
      }
    });
  };

  return (
    <div style={styles.container}>
      {/* En-tête amélioré */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>{t('costBreakdown.title')}</h2>
          <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px', marginBottom: 0 }}>
            {t('costBreakdown.subtitle')}
          </p>
        </div>
        <div style={styles.totalSection}>
          <div style={styles.totalAmount}>{formatCost(totalMonthly)}</div>
          <div style={styles.totalLabel}>{t('costBreakdown.perMonth')}</div>
          <div style={styles.hourlyAmount}>{formatCost(totalHourly)}/{t('costBreakdown.perHour')}</div>
        </div>
      </div>

      {/* Contrôles de tri */}
      <div style={styles.controls}>
        <label style={styles.controlLabel}>
          {t('costBreakdown.sortBy')}:
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'cost' | 'name')}
            style={styles.select}
          >
            <option value="cost">{t('costBreakdown.sortByCost')}</option>
            <option value="name">{t('costBreakdown.sortByName')}</option>
          </select>
        </label>
      </div>

      {/* Liste des providers */}
      <div style={styles.providerList}>
        {sortedProviders.map(([provider, data]) => {
          const isExpanded = expandedProvider === provider;
          const percentage = ((data.total / totalMonthly) * 100).toFixed(1);

          return (
            <div
              key={provider}
              style={{
                ...styles.providerCard,
                transform: isExpanded ? 'scale(1.01)' : 'scale(1)',
                boxShadow: isExpanded ? '0 8px 24px rgba(0, 0, 0, 0.15)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease'
              }}
            >
              {/* Header du provider - amélioré */}
              <div
                style={{
                  ...styles.providerHeader,
                  backgroundColor: isExpanded ? '#f8fafc' : '#f9fafb',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onClick={() => toggleProvider(provider)}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isExpanded ? '#f8fafc' : '#f9fafb'}
              >
                <div style={styles.providerInfo}>
                  <div
                    style={{
                      ...styles.providerIcon,
                      backgroundColor: getProviderColor(provider),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '20px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      transform: isExpanded ? 'rotate(5deg) scale(1.1)' : 'rotate(0) scale(1)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {getProviderIcon(provider)}
                  </div>
                  <div>
                    <div style={styles.providerName}>
                      {getProviderName(provider)}
                    </div>
                    <div style={styles.providerStats}>
                      📦 {data.items.length} {data.items.length > 1 ? t('costBreakdown.resources_plural') : t('costBreakdown.resources')} · 📊 {percentage}% {t('costBreakdown.ofTotal')}
                    </div>
                  </div>
                </div>
                <div style={styles.providerCost}>
                  <div style={{ color: getProviderColor(provider), fontWeight: 700 }}>
                    {formatCost(data.total)}/{t('costBreakdown.perMonth')}
                  </div>
                  <div style={{
                    ...styles.expandIcon,
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
                    transition: 'transform 0.3s ease'
                  }}>
                    ▼
                  </div>
                </div>
              </div>

              {/* Barre de progression améliorée */}
              <div style={styles.progressBar}>
                <div
                  style={{
                    ...styles.progressFill,
                    width: `${percentage}%`,
                    background: `linear-gradient(90deg, ${getProviderColor(provider)}, ${getProviderColor(provider)}dd)`,
                    transition: 'width 0.5s ease',
                    boxShadow: isExpanded ? `0 2px 8px ${getProviderColor(provider)}66` : 'none'
                  }}
                />
              </div>

              {/* Liste des ressources (collapsible) - améliorée */}
              {isExpanded && (
                <div
                  style={{
                    ...styles.resourceList,
                    animation: 'slideDown 0.3s ease',
                    maxHeight: isExpanded ? '1000px' : '0',
                    overflow: 'hidden',
                    transition: 'max-height 0.3s ease'
                  }}
                >
                  {sortItems(data.items).map((item, index) => (
                    <div
                      key={item.resourceId}
                      style={{
                        ...styles.resourceItem,
                        animationDelay: `${index * 0.05}s`,
                        animation: 'fadeInUp 0.3s ease forwards',
                        opacity: 0
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f8fafc';
                        e.currentTarget.style.transform = 'translateX(4px)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.transform = 'translateX(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <div style={styles.resourceInfo}>
                        <div style={{
                          ...styles.resourceName,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <span style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            backgroundColor: getProviderColor(provider),
                            display: 'inline-block'
                          }} />
                          {item.name}
                        </div>
                        <div style={styles.resourceMeta}>
                          <span style={styles.resourceType}>🔧 {item.resourceType}</span>
                          <span style={styles.resourceSeparator}>·</span>
                          <span style={styles.resourceSku}>📋 {item.sku}</span>
                          <span style={styles.resourceSeparator}>·</span>
                          <span style={styles.resourceRegion}>🌍 {item.region}</span>
                        </div>
                      </div>
                      <div style={styles.resourceCost}>
                        <div style={{
                          ...styles.monthlyCost,
                          color: getProviderColor(provider),
                          fontWeight: 700
                        }}>
                          {formatCost(item.monthly || 0)}/{t('costBreakdown.perMonth')}
                        </div>
                        <div style={styles.hourlyCost}>
                          {formatCost(item.hourly || 0)}/{t('costBreakdown.perHour')}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <style>
                {`
                  @keyframes slideDown {
                    from {
                      opacity: 0;
                      max-height: 0;
                    }
                    to {
                      opacity: 1;
                      max-height: 1000px;
                    }
                  }

                  @keyframes fadeInUp {
                    from {
                      opacity: 0;
                      transform: translateY(10px);
                    }
                    to {
                      opacity: 1;
                      transform: translateY(0);
                    }
                  }
                `}
              </style>
            </div>
          );
        })}
      </div>

      {/* Disclaimer */}
      <div style={styles.disclaimer}>
        {t('messages.disclaimer')}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    padding: '24px',
    maxWidth: '900px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    paddingBottom: '16px',
    borderBottom: '2px solid #e5e7eb',
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: 700,
    color: '#111827',
  },
  totalSection: {
    textAlign: 'right',
  },
  totalAmount: {
    fontSize: '32px',
    fontWeight: 700,
    color: '#10b981',
  },
  totalLabel: {
    fontSize: '14px',
    color: '#6b7280',
    marginTop: '4px',
  },
  hourlyAmount: {
    fontSize: '14px',
    color: '#9ca3af',
    marginTop: '4px',
  },
  controls: {
    marginBottom: '16px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  controlLabel: {
    fontSize: '14px',
    color: '#6b7280',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  select: {
    padding: '6px 12px',
    borderRadius: '4px',
    border: '1px solid #d1d5db',
    fontSize: '14px',
    cursor: 'pointer',
  },
  providerList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  providerCard: {
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    overflow: 'hidden',
    transition: 'box-shadow 0.2s',
  },
  providerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    cursor: 'pointer',
    backgroundColor: '#f9fafb',
    transition: 'background-color 0.2s',
  },
  providerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  providerIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  providerName: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#111827',
  },
  providerStats: {
    fontSize: '13px',
    color: '#6b7280',
    marginTop: '4px',
  },
  providerCost: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    fontSize: '18px',
    fontWeight: 600,
    color: '#111827',
  },
  expandIcon: {
    fontSize: '14px',
    color: '#9ca3af',
  },
  progressBar: {
    height: '4px',
    backgroundColor: '#e5e7eb',
    width: '100%',
  },
  progressFill: {
    height: '100%',
    transition: 'width 0.3s ease',
  },
  resourceList: {
    padding: '8px',
    backgroundColor: '#ffffff',
  },
  resourceItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    borderRadius: '6px',
    transition: 'background-color 0.2s',
    cursor: 'pointer',
  },
  resourceInfo: {
    flex: 1,
  },
  resourceName: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#111827',
    marginBottom: '4px',
  },
  resourceMeta: {
    fontSize: '12px',
    color: '#6b7280',
  },
  resourceType: {
    fontFamily: 'monospace',
  },
  resourceSeparator: {
    margin: '0 6px',
  },
  resourceSku: {
    fontWeight: 500,
  },
  resourceRegion: {
    textTransform: 'uppercase',
  },
  resourceCost: {
    textAlign: 'right',
  },
  monthlyCost: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#111827',
  },
  hourlyCost: {
    fontSize: '12px',
    color: '#9ca3af',
    marginTop: '2px',
  },
  disclaimer: {
    marginTop: '24px',
    padding: '12px',
    backgroundColor: '#fef3c7',
    borderRadius: '6px',
    fontSize: '12px',
    color: '#92400e',
    lineHeight: '1.5',
  },
};
